import React from 'react';
import classNames from 'classnames';
import Link from '../../Link';
import hoverArrow from '@/images/hover-arrow-button-bento.svg';
import Image from 'next/image';

export interface ArrowIconProps {
  className?: string; // applied to the <svg>
  containerClassName?: string; // applied to an optional wrapping element
}

export const ArrowIcon: React.FC<ArrowIconProps> = ({
  className,
  containerClassName
}) => {
  const svg = (
    <Image
      src={hoverArrow}
      alt="Hover Arrow"
      aria-hidden="true"
      className={className}
      layout="fixed"
      width={16}
      height={16}
      unoptimized={process.env.NODE_ENV === 'test'}
    />
  );

  return containerClassName ? (
    <span className={containerClassName}>{svg}</span>
  ) : (
    svg
  );
};

export interface TextLinkProps {
  href: string;
  children: React.ReactNode;
  className?: string;
  showArrow?: boolean;
  arrowColor?: string;
  underline?: boolean;
  target?: string;
  rel?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export const TextLink = React.forwardRef<HTMLAnchorElement, TextLinkProps>(
  (
    {
      href,
      children,
      className,
      showArrow = false,
      arrowColor = 'dark',
      underline = true,
      target,
      rel,
      onClick,
      ...props
    },
    ref
  ) => {
    const arrowSvgClasses = classNames(
      'inline-block -translate-x-1 opacity-0 transition-all duration-200 group-hover:translate-x-0.5 group-hover:opacity-100',
      arrowColor === 'light' ? 'invert' : ''
    );
    const arrowContainerClasses = classNames(
      'ml-1 inline-flex h-4 w-4 items-center justify-center align-middle'
    );

    return (
      <Link
        ref={ref}
        href={href}
        target={target}
        rel={rel}
        onClick={onClick}
        className={classNames(
          'focus:ring-offset-current group -ml-1 inline-flex w-fit items-center text-nowrap rounded-button-shape p-1 focus:outline-none focus:ring-2 focus:ring-accent4-base focus:ring-offset-1',
          underline &&
            'underline decoration-[0.08em] underline-offset-[0.125em]',
          className
        )}
        {...props}
      >
        {children}
        {showArrow && (
          <ArrowIcon
            className={arrowSvgClasses}
            containerClassName={arrowContainerClasses}
          />
        )}
      </Link>
    );
  }
);

TextLink.displayName = 'TextLink';
